/**
 * @file Product category and display order related constants.
 */

/**
 * Product category types for album folder products.
 *
 * @enum {string}
 */
const PRODUCT_CATEGORY_TYPES = {
  PRINT_PACKAGE: 'PRINT_PACKAGE',
  CUSTOM_PRINT: 'CUSTOM_PRINT',
  SPECIALTY_PRINT: 'SPECIALTY_PRINT',
  DIGITAL_ITEM: 'DIGITAL_ITEM',
};

/**
 * Digital product types for digital items category.
 *
 * @enum {string}
 */
const DIGITAL_PRODUCT_TYPES = {
  DIGITAL_BUNDLE: 'DIGITAL_BUNDLE',
  DIGITAL_PHOTO_PASS: 'DIGITAL_PHOTO_PASS',
  INDIVIDUAL_DOWNLOAD: 'INDIVIDUAL_DOWNLOAD',
};

/**
 * Default display order for product categories.
 *
 * @type {string[]}
 */
const DEFAULT_PRODUCT_CATEGORY_ORDER = [
  PRODUCT_CATEGORY_TYPES.PRINT_PACKAGE,
  PRODUCT_CATEGORY_TYPES.CUSTOM_PRINT,
  PRODUCT_CATEGORY_TYPES.SPECIALTY_PRINT,
  PRODUCT_CATEGORY_TYPES.DIGITAL_ITEM,
];

/**
 * Default display order for digital product types.
 *
 * @type {string[]}
 */
const DEFAULT_DIGITAL_PRODUCT_ORDER = [
  DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE,
  DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS,
  DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD,
];

/**
 * Display labels for product category types.
 *
 * @type {Object.<string, string>}
 */
const PRODUCT_CATEGORY_LABELS = {
  [PRODUCT_CATEGORY_TYPES.PRINT_PACKAGE]: 'Print Packages',
  [PRODUCT_CATEGORY_TYPES.CUSTOM_PRINT]: 'Custom Prints & Products',
  [PRODUCT_CATEGORY_TYPES.SPECIALTY_PRINT]: 'Individual Prints & Products',
  [PRODUCT_CATEGORY_TYPES.DIGITAL_ITEM]: 'Digital Only',
};

/**
 * Display labels for digital product types.
 *
 * @type {Object.<string, string>}
 */
const DIGITAL_PRODUCT_TYPE_LABELS = {
  [DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE]: 'Digital Bundles',
  [DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS]: 'Digital Photo Pass',
  [DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD]: 'Individual Downloads',
};

/**
 * Consumer-facing category header labels (may differ from admin labels).
 *
 * @type {Object.<string, string>}
 */
const CONSUMER_CATEGORY_HEADERS = {
  [PRODUCT_CATEGORY_TYPES.CUSTOM_PRINT]:
    'Memory Mates, Collages & Vendor-fulfilled Products',
  [PRODUCT_CATEGORY_TYPES.SPECIALTY_PRINT]: 'Individual Prints',
  [PRODUCT_CATEGORY_TYPES.DIGITAL_ITEM]: 'Digital Only',
};

/**
 * Configuration for building digital product type items.
 * Maps digital product type to its data source in albumFolder.
 *
 * @type {Object.<string, {check: Function, label: string}>}
 */
const DIGITAL_PRODUCT_TYPE_CONFIG = {
  [DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE]: {
    check: albumFolder => albumFolder.digital_bundles?.records?.length > 0,
    label: DIGITAL_PRODUCT_TYPE_LABELS[DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE],
  },
  [DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS]: {
    check: albumFolder => albumFolder.price?.value > 0,
    label:
      DIGITAL_PRODUCT_TYPE_LABELS[DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS],
  },
  [DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD]: {
    check: albumFolder =>
      albumFolder.photo_pricing?.digital_downloads_price?.value > 0 ||
      albumFolder.web_gallery_price_per_photo?.value > 0,
    label:
      DIGITAL_PRODUCT_TYPE_LABELS[DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD],
  },
};

export {
  PRODUCT_CATEGORY_TYPES,
  DIGITAL_PRODUCT_TYPES,
  DEFAULT_PRODUCT_CATEGORY_ORDER,
  DEFAULT_DIGITAL_PRODUCT_ORDER,
  PRODUCT_CATEGORY_LABELS,
  DIGITAL_PRODUCT_TYPE_LABELS,
  CONSUMER_CATEGORY_HEADERS,
  DIGITAL_PRODUCT_TYPE_CONFIG,
};
